import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Avatar, { AvatarGroup } from '@/components/common/Avatar';
import { BrandFavicon } from '../BrandFavicon';
import { useBrandLogoHost } from '../../context/BrandLogoContext';
import { brandInitial } from '../../utils/format';
import { faviconUrl } from '../../utils/favicon';
import { normalizeBrandRelationship } from '../../utils/brand-relationship';
import type { BrandRelationship, PromptListTopBrand } from '../../types';

const RELATIONSHIP_LABELS: Record<BrandRelationship, string> = {
  you: 'شما',
  competitor: 'رقیب',
  known: 'شناخته‌شده',
  detected: 'شناسایی‌شده',
};

function brandKey(brand: PromptListTopBrand): string {
  return brand.knownBrandId ?? brand.canonicalName.toLowerCase();
}

type TooltipPlacement = 'above' | 'below';

function BrandStackAvatar({ brand }: { brand: PromptListTopBrand }) {
  const host = useBrandLogoHost(brand.canonicalName, brand.domain, {
    relationship: brand.relationship,
  });
  const candidates = useMemo(
    () =>
      [brand.faviconUrl, faviconUrl(host)].filter(
        (value, index, arr): value is string => Boolean(value) && arr.indexOf(value) === index,
      ),
    [brand.faviconUrl, host],
  );
  const [attempt, setAttempt] = useState(0);
  const src = candidates[attempt];

  useEffect(() => {
    setAttempt(0);
  }, [brand.faviconUrl, host]);

  if (!src) {
    return (
      <Avatar
        size="xl"
        name={brandInitial(brand.canonicalName)}
        tooltip={brand.canonicalName}
        isExact
        className="border border-3 rounded-circle border-200"
      />
    );
  }

  return (
    <div dir="ltr" className="avatar avatar-xl border border-3 rounded-circle border-200">
      <img
        className="rounded-circle"
        src={src}
        alt={brand.canonicalName}
        title={brand.canonicalName}
        onError={() => setAttempt(current => current + 1)}
      />
    </div>
  );
}

export function TopBrandAvatars({
  brands,
  brandsFound,
  max = 3,
  showHoverTooltip = true,
  className,
}: {
  brands: PromptListTopBrand[];
  brandsFound?: number;
  max?: number;
  /** When false, only the avatar stack is rendered (parent can own the popover). */
  showHoverTooltip?: boolean;
  className?: string;
}) {
  const [hover, setHover] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
    placement: TooltipPlacement;
  } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<number | null>(null);

  function clearHideTimeout() {
    if (hideTimeoutRef.current != null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }

  function updateTooltipPosition() {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const tooltipHeight = Math.min(360, window.innerHeight * 0.5);
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: TooltipPlacement =
      spaceBelow < tooltipHeight + 16 && rect.top > tooltipHeight + 16 ? 'above' : 'below';

    setTooltipPos({
      left: rect.left,
      top: placement === 'below' ? rect.bottom + 8 : rect.top - 8,
      placement,
    });
  }

  function openTooltip() {
    if (!showHoverTooltip) return;
    clearHideTimeout();
    updateTooltipPosition();
    setHover(true);
  }

  function scheduleClose() {
    if (!showHoverTooltip) return;
    clearHideTimeout();
    hideTimeoutRef.current = window.setTimeout(() => {
      setHover(false);
      setTooltipPos(null);
    }, 150);
  }

  useLayoutEffect(() => {
    if (!hover || !showHoverTooltip) return;

    updateTooltipPosition();

    function handleReposition() {
      updateTooltipPosition();
    }

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [hover, brands.length, showHoverTooltip]);

  if (!brands.length) {
    return <span className="fs--2 text-300">—</span>;
  }

  const visible = brands.slice(0, max);
  const totalCount = brandsFound ?? brands.length;
  const extra = Math.max(0, totalCount - visible.length);

  const stack = (
    <AvatarGroup className={className ?? 'justify-content-end'}>
      {visible.map(brand => (
        <BrandStackAvatar key={brandKey(brand)} brand={brand} />
      ))}
      {extra > 0 && (
        <Avatar
          size="xl"
          name={`+${extra}`}
          isExact
          className="border border-3 rounded-circle border-200"
        />
      )}
    </AvatarGroup>
  );

  if (!showHoverTooltip) {
    return stack;
  }

  const tooltip =
    hover && tooltipPos
      ? createPortal(
          <div
            className="position-fixed rounded-3 border bg-white dark__bg-card-dark p-2 shadow-lg text-start"
            dir="rtl"
            style={{
              left: tooltipPos.left,
              top: tooltipPos.top,
              zIndex: 1100,
              minWidth: 220,
              maxWidth: 'min(320px, calc(100vw - 2rem))',
              transform: tooltipPos.placement === 'above' ? 'translateY(-100%)' : undefined,
            }}
            onMouseEnter={openTooltip}
            onMouseLeave={scheduleClose}
          >
            <p className="px-2 pb-1 mb-1 fs--2 fw-semibold text-uppercase text-400">برندهای برتر</p>
            <ul className="list-unstyled mb-0 overflow-y-auto" style={{ maxHeight: 'min(360px, 50vh)' }}>
              {brands.map(brand => (
                <li
                  key={`${brandKey(brand)}-tip`}
                  className="d-flex align-items-center gap-2 px-2 py-1 rounded-2 mb-1"
                >
                  <BrandFavicon
                    host={brand.domain}
                    faviconUrl={brand.faviconUrl}
                    name={brand.canonicalName}
                    relationship={brand.relationship}
                    colorIndex={0}
                  />
                  <div className="min-w-0">
                    <p className="fs--1 fw-medium text-800 text-truncate mb-0">{brand.canonicalName}</p>
                    <p className="fs--2 text-700 mb-0">
                      {RELATIONSHIP_LABELS[normalizeBrandRelationship(brand.relationship)]}
                      {brand.avgPosition != null ? ` · #${brand.avgPosition}` : ''}
                      {` · ${brand.coverage} · ${brand.visibilityScore}%`}
                    </p>
                  </div>
                </li>
              ))}
              {extra > 0 && (
                <li className="px-2 py-1 fs--2 text-400">+{extra} مورد دیگر نمایش داده نشده</li>
              )}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={triggerRef}
      className="position-relative d-inline-flex"
      onMouseEnter={openTooltip}
      onMouseLeave={scheduleClose}
    >
      {stack}
      {tooltip}
    </div>
  );
}
